/**
 * Company-wide Payments List
 */
import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { CreditCard, Search, Loader2, ChevronLeft, ChevronRight } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const formatCurrency = (value) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(value || 0);
const formatDate = (dateStr) => dateStr ? new Date(dateStr).toLocaleDateString() : '—';

const STATUS_CONFIG = {
  draft: { label: 'Draft', color: 'bg-slate-100 text-slate-600' },
  posted: { label: 'Posted', color: 'bg-green-100 text-green-700' },
  voided: { label: 'Voided', color: 'bg-red-100 text-red-700' }
};

const StatusChip = ({ status }) => {
  const config = STATUS_CONFIG[status] || { label: status, color: 'bg-slate-100 text-slate-600' };
  return <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${config.color}`}>{config.label}</span>;
};

const PAYMENT_METHODS = {
  check: 'Check',
  credit_card: 'Credit Card',
  ach: 'ACH',
  wire: 'Wire',
  cash: 'Cash',
  other: 'Other'
};

export default function PaymentsList({ user }) {
  const [payments, setPayments] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ status: '', search: '', skip: 0, limit: 25 });

  const fetchPayments = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filters.status) params.append('status', filters.status);
      if (filters.search) params.append('search', filters.search);
      params.append('skip', filters.skip);
      params.append('limit', filters.limit);

      const response = await axios.get(`${API}/finance/hub/payments?${params}`);
      setPayments(response.data.items);
      setTotal(response.data.total);
    } catch (err) {
      console.error('Error fetching payments:', err);
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => { fetchPayments(); }, [fetchPayments]);

  const handleSearch = (e) => {
    if (e.key === 'Enter') {
      setFilters({ ...filters, skip: 0 });
    }
  };

  const totalPages = Math.ceil(total / filters.limit);
  const currentPage = Math.floor(filters.skip / filters.limit) + 1;

  return (
    <Card data-testid="payments-list">
      <CardHeader className="flex flex-row items-center justify-between py-4">
        <CardTitle className="text-base font-semibold flex items-center gap-2">
          <CreditCard className="h-4 w-4 text-green-500" />
          All Payments ({total})
        </CardTitle>
      </CardHeader>
      <CardContent>
        {/* Filters */}
        <div className="flex gap-3 mb-4">
          <div className="relative flex-1 max-w-xs">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <Input
              placeholder="Search payments..."
              className="pl-9"
              value={filters.search}
              onChange={(e) => setFilters({ ...filters, search: e.target.value })}
              onKeyDown={handleSearch}
            />
          </div>
          <Select value={filters.status} onValueChange={(v) => setFilters({ ...filters, status: v === 'all' ? '' : v, skip: 0 })}>
            <SelectTrigger className="w-[150px]">
              <SelectValue placeholder="All Status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Status</SelectItem>
              <SelectItem value="posted">Posted</SelectItem>
              <SelectItem value="voided">Voided</SelectItem>
            </SelectContent>
          </Select>
          <Button variant="outline" onClick={fetchPayments}>Refresh</Button>
        </div>

        {/* Table */}
        {loading ? (
          <div className="flex justify-center py-8"><Loader2 className="h-6 w-6 animate-spin" /></div>
        ) : payments.length > 0 ? (
          <>
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Payment #</TableHead>
                    <TableHead>Customer</TableHead>
                    <TableHead>Project</TableHead>
                    <TableHead>Date</TableHead>
                    <TableHead>Method</TableHead>
                    <TableHead>Reference</TableHead>
                    <TableHead className="text-right">Amount</TableHead>
                    <TableHead>Status</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {payments.map((pmt) => (
                    <TableRow key={pmt.id} className="hover:bg-slate-50 dark:hover:bg-slate-800/50">
                      <TableCell className="font-medium">{pmt.transaction_number || '—'}</TableCell>
                      <TableCell>{pmt.contact_name || '—'}</TableCell>
                      <TableCell className="text-slate-500">{pmt.project_name || '—'}</TableCell>
                      <TableCell className="text-slate-500">{formatDate(pmt.transaction_date)}</TableCell>
                      <TableCell className="capitalize">{PAYMENT_METHODS[pmt.payment_method] || pmt.payment_method || '—'}</TableCell>
                      <TableCell className="text-slate-500">{pmt.reference_number || '—'}</TableCell>
                      <TableCell className="text-right font-medium text-green-600">
                        {formatCurrency(pmt.total_amount)}
                      </TableCell>
                      <TableCell><StatusChip status={pmt.status} /></TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>

            {/* Pagination */}
            <div className="flex items-center justify-between mt-4">
              <p className="text-sm text-slate-500">
                Showing {filters.skip + 1}-{Math.min(filters.skip + filters.limit, total)} of {total}
              </p>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  disabled={currentPage === 1}
                  onClick={() => setFilters({ ...filters, skip: filters.skip - filters.limit })}
                >
                  <ChevronLeft className="h-4 w-4" />
                </Button>
                <span className="px-3 py-1 text-sm">Page {currentPage} of {totalPages || 1}</span>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={currentPage >= totalPages}
                  onClick={() => setFilters({ ...filters, skip: filters.skip + filters.limit })}
                >
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            </div>
          </>
        ) : (
          <div className="text-center py-8 text-slate-500">No payments found</div>
        )}
      </CardContent>
    </Card>
  );
}
